import { defineModule } from '@/js/utils/helpers';

/**
 * Blocks marked `data-ip-reveal` rise and fade once as they enter the viewport.
 * The transition itself lives in css/theme/motion.css; this only adds `.is-in`.
 *
 * The observer alone is not enough: a jump — an anchor link, a restored scroll
 * position, a programmatic scroll — moves past a block without it ever
 * intersecting, which would leave it invisible for good. The scroll pass
 * therefore sweeps up anything the viewport has already reached.
 */

let observer: IntersectionObserver | null = null;
let pending: HTMLElement[] = [];
let frame = 0;

const reveal = (target: HTMLElement) => {
  target.classList.add('is-in');
  observer?.unobserve(target);
};

const flush = () => {
  frame = 0;
  pending = pending.filter((target) => {
    if (target.getBoundingClientRect().top >= window.innerHeight) return true;
    reveal(target);
    return false;
  });
};

const onScroll = () => {
  if (frame) return;
  frame = requestAnimationFrame(flush);
};

export default defineModule(
  () => {
    const targets = Array.from(
      document.querySelectorAll<HTMLElement>('[data-ip-reveal]'),
    );
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach((target) => target.classList.add('is-in'));
      return;
    }

    pending = targets;
    observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const target = entry.target as HTMLElement;
          reveal(target);
          pending = pending.filter((item) => item !== target);
        }),
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 },
    );

    targets.forEach((target) => observer?.observe(target));
    window.addEventListener('scroll', onScroll, { passive: true });
  },
  () => {
    window.removeEventListener('scroll', onScroll);
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    observer?.disconnect();
    observer = null;
    pending = [];
  },
);
